import React from "react";
import { useTable, FilterProps, useFilters } from "react-table";

interface TableProps {
  children?: React.ReactNode;
  className?: string;
}
interface TableCellProps extends TableProps {
  nowrap?: boolean;
}

export const TableHead = ({
  children,
  ...props
}: TableProps & React.HTMLAttributes<HTMLTableSectionElement>) => (
  <thead {...props}>{children}</thead>
);
export const TableRow = ({
  children,
  className,
  ...props
}: TableProps & React.HTMLAttributes<HTMLTableRowElement>) => (
  <tr className={className} {...props}>
    {children}
  </tr>
);

export const TableHeaderCell = ({
  nowrap,
  className,
  children,
  ...props
}: TableCellProps & React.HTMLAttributes<HTMLTableHeaderCellElement>) => (
  <th
    className={`${nowrap ? "whitespace-no-wrap " : ""}${
      className ? className + " " : ""
    }px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase`}
    {...props}
  >
    {children}
  </th>
);

export const TableCell = ({
  nowrap = false,
  className,
  children,
  ...props
}: TableCellProps & React.HTMLAttributes<HTMLTableCellElement>) => (
  <td
    className={`${nowrap ? "whitespace-no-wrap " : ""}${
      className ? className + " " : ""
    }px-6 py-4 border-b border-gray-200 text-sm leading-5 align-text-top`}
    {...props}
  >
    {children}
  </td>
);

export const TableBody = ({
  children,
  className,
  ...props
}: TableProps & React.HTMLAttributes<HTMLTableSectionElement>) => (
  <tbody className={`${className ? className + " " : ""}bg-white`} {...props}>
    {children}
  </tbody>
);

export const Table = ({
  children,
  className,
  ...props
}: TableProps & React.HTMLAttributes<HTMLTableElement>) => (
  <table
    className={`${className ? className + " " : ""}w-full mb-6`}
    {...props}
  >
    {children}
  </table>
);

export type LogDataSelect = ColumnDetails & {
  subRows?: ColumnDetails[];
};

interface ColumnDetails {
  [key: string]: string;
}

interface TableConfig {
  columns: (
    | {
        Header: string;
        accessor: string;
        Filter?: undefined;
        filter?: undefined;
      }
    | {
        Header: string;
        accessor: string;
        Filter: ({
          column: { filterValue, setFilter, preFilteredRows, id },
        }: FilterProps<LogDataSelect>) => JSX.Element;
        filter: string;
      }
  )[];
  data: ColumnDetails[];
  isPaused: boolean;
}

export function TableLogs({ columns, data, isPaused }: TableConfig) {
  const { rows, headerGroups, prepareRow } = useTable(
    { columns, data },
    useFilters
    // useGlobalFilter
  );

  return (
    <>
      <Table>
        <TableHead>
          <TableRow>
            <TableHeaderCell className="w-1/8" nowrap>
              Log Date
            </TableHeaderCell>
            <TableHeaderCell className="w-1/8">
              Since Device Start
            </TableHeaderCell>
            <TableHeaderCell className="w-1/8">Priority</TableHeaderCell>
            <TableHeaderCell className="w-1/8">Tag</TableHeaderCell>
            <TableHeaderCell className="w-1/2">Message</TableHeaderCell>
          </TableRow>
          <TableRow>
            {headerGroups.map((headerGroup) =>
              headerGroup.headers.map((column, index) => (
                <TableHeaderCell key={index} className="w-1/8">
                  {column.canFilter && isPaused
                    ? column.render("Filter")
                    : "N/A"}
                </TableHeaderCell>
              ))
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, rowIndex) => {
            prepareRow(row);
            return (
              <TableRow key={rowIndex}>
                {row.cells.map((cell, cellIndex) => {
                  return (
                    <TableCell key={`${rowIndex}-${cellIndex}`}>
                      {cell.render("Cell")}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </>
  );
}
